import { useMemo } from "react";
import { Chart } from "@highcharts/react";
import type { Options, SeriesAreaOptions } from "highcharts/highcharts.src";
import { clamp } from "../../../utils/math";

interface SparklineChartProps {
  data: Array<number | null | undefined>;
  color?: string;
  width?: number;
  height?: number | null;
  min?: number;
  max?: number;
  highlightFirstAndLastPoint?: boolean;
  markerColor?: string;
}

const normalizeColor = (value?: string) => {
  if (!value) return undefined;
  const rgbaMatch = /^#[0-9a-fA-F]{8}$/;
  if (!rgbaMatch.test(value)) return value;
  const r = parseInt(value.slice(1, 3), 16);
  const g = parseInt(value.slice(3, 5), 16);
  const b = parseInt(value.slice(5, 7), 16);
  const a = parseInt(value.slice(7, 9), 16) / 255;
  return `rgba(${r}, ${g}, ${b}, ${Number.isFinite(a) ? a.toFixed(3) : 1})`;
};

const SparklineChart = ({
  data,
  color = "#0070f3",
  width,
  height = null,
  min,
  max,
  highlightFirstAndLastPoint = false,
  markerColor,
}: SparklineChartProps) => {
  const options = useMemo<Options>(() => {
    const sanitizedColor = normalizeColor(color);
    const sanitizedMarkerColor = normalizeColor(markerColor ?? color);
    const originalData = data.map((point) => point ?? null);
    const clampValue = (value: number | null) => (value == null ? null : clamp(value, min, max));

    const areaSeries: SeriesAreaOptions = {
      type: "area",
      color: sanitizedColor,
      lineColor: sanitizedColor,
      lineWidth: 3,
      fillOpacity: 0.15,
      enableMouseTracking: false,
      animation: false,
    };

    const clampedData = originalData.map((value) => clampValue(value));
    const highlightIndices: number[] = [];

    if (highlightFirstAndLastPoint) {
      const firstIndex = clampedData.findIndex((value) => value != null);
      let lastIndex = -1;
      for (let i = clampedData.length - 1; i >= 0; i -= 1) {
        if (clampedData[i] != null) {
          lastIndex = i;
          break;
        }
      }

      if (firstIndex !== -1) {
        highlightIndices.push(firstIndex);
        if (lastIndex !== -1 && lastIndex !== firstIndex) {
          highlightIndices.push(lastIndex);
        }
      }
    }

    const highlightedIndexSet = new Set(highlightIndices);

    areaSeries.data = clampedData.map((value, index) => {
      if (value == null) return null;
      if (highlightedIndexSet.has(index)) {
        return {
          y: value,
          marker: {
            enabled: true,
            radius: 4,
            lineWidth: 0,
            fillColor: sanitizedMarkerColor,
          },
        };
      }
      return value;
    });

    areaSeries.marker = {
      enabled: false,
    };

    return {
      chart: {
        backgroundColor: "transparent",
        height,
        width,
        spacing: [4, 4, 4, 4],
        margin: [4, 4, 4, 4],
        style: {
          overflow: "hidden",
        },
      },
      title: {
        text: undefined,
      },
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
      xAxis: {
        visible: false,
        startOnTick: false,
        endOnTick: false,
        minPadding: 0.08,
        maxPadding: 0.08,
      },
      yAxis: {
        visible: false,
        startOnTick: false,
        endOnTick: false,
        min,
        max,
      },
      plotOptions: {
        series: {
          animation: false,
          clip: false,
          turboThreshold: data.length + 1,
        },
      },
      series: [areaSeries],
    };
  }, [color, data, height, highlightFirstAndLastPoint, markerColor, max, min, width]);

  return (
    <Chart
      options={options}
      containerProps={{
        style: {
          width: width ? `${width}px` : "100%",
          height: height !== null ? `${height}px` : "100%",
          flex: 1,
        },
      }}
    />
  );
};

export default SparklineChart;
